import { useId, type JSX } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import type { DataKey } from 'recharts/types/util/types';
import { safeNum } from '@heyroot/reporting-core';
import { METRIC_CHART_COLOR, type MetricClass } from '../chart-palette.js';

export interface TrendLineChartProps<TRow extends Record<string, unknown>> {
  data: ReadonlyArray<TRow>;
  xKey: keyof TRow & string;
  yKey: keyof TRow & string;
  metricClass: MetricClass;
  height?: number;
  /**
   * Visual treatment:
   * - `'axes'` (default) — gridlines + X/Y axes + a stroke line.
   * - `'sparkline'` — de-chromed gradient area, hero/KPI look.
   * - `'line'` — de-chromed stroke line, NO axes/grid/gradient, with a single
   *   end-dot. For compact trend tiles (avg-ticket) where the shape + latest
   *   point are the message. Hover still surfaces exact values.
   */
  variant?: 'axes' | 'sparkline' | 'line';
  /**
   * Formats a value for the hover tooltip (and the y-axis ticks of the `'axes'`
   * variant when no `yTickFormatter` is given). Receives the chart's raw number
   * in whatever unit the row carries; the caller owns that unit. A money chart
   * passes a currency formatter; a count/share/duration chart passes
   * `formatChartNumber` (or its own). Required, so `tsc` rejects a chart that
   * would print Recharts' bare raw number (`gross : 17`).
   */
  valueFormatter: (value: number) => string;
  /**
   * Optional formatter for the y-axis tick labels of the `'axes'` variant, e.g.
   * a compact `$1.2k` where the tooltip's `valueFormatter` prints the full
   * `$1,234.56`. Falls back to `valueFormatter`. Same contract as
   * `VolumeBarChart.yTickFormatter`.
   */
  yTickFormatter?: (value: number) => string;
  /** Tooltip label for the series; omitted → Recharts shows the raw `yKey`. */
  name?: string;
}

export function TrendLineChart<TRow extends Record<string, unknown>>(
  props: TrendLineChartProps<TRow>,
): JSX.Element {
  const variant = props.variant ?? 'axes';
  // Stable, collision-free gradient id (colons from useId are invalid in an
  // SVG url(#…) reference, so strip them).
  const gradientId = `trend-fill-${useId().replace(/:/g, '')}`;

  if (props.data.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-control border border-dashed border-border bg-surface text-caption text-muted"
        style={{ height: props.height ?? (variant === 'axes' ? 240 : 160) }}
      >
        No data for this period.
      </div>
    );
  }
  // 'line' preserves explicit null/undefined y-values so `connectNulls={false}`
  // renders zero-order days as gaps rather than coercing them to 0 (safeNum's
  // default), per spec §4.2. 'axes'/'sparkline' keep the existing safeNum coercion.
  const data = props.data.map((r) => {
    const rawY = (r as Record<string, number | null | undefined>)[props.yKey];
    return {
      ...r,
      [props.yKey]: variant === 'line' ? (rawY ?? null) : safeNum(rawY),
    };
  });
  const color = METRIC_CHART_COLOR[props.metricClass];
  const { valueFormatter } = props;
  const tickFormatter = props.yTickFormatter ?? valueFormatter;
  // The sparkline and line variants draw no XAxis, so Recharts would label the hover with the row
  // index ("0" above the only point of a one-day series): name the row by its x value instead. The
  // axes variant's XAxis already supplies that label.
  const rowLabel =
    variant === 'axes'
      ? {}
      : {
          labelFormatter: (_label: unknown, payload: ReadonlyArray<{ payload?: unknown }>) => {
            const row = payload[0]?.payload as Record<string, unknown> | undefined;
            const x = row?.[props.xKey];
            return x == null ? '' : String(x);
          },
        };
  const tooltip = (
    <Tooltip
      {...rowLabel}
      formatter={(v: unknown) => (v == null ? '' : valueFormatter(Number(v)))}
    />
  );
  const seriesName = props.name !== undefined ? { name: props.name } : {};

  if (variant === 'sparkline') {
    return (
      <ResponsiveContainer width="100%" height={props.height ?? 160}>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.24} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {tooltip}
          <Area
            type="monotone"
            {...seriesName}
            dataKey={props.yKey as DataKey<TRow>}
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (variant === 'line') {
    const lastIndex = data.length - 1;
    return (
      <ResponsiveContainer width="100%" height={props.height ?? 160}>
        <LineChart data={data} margin={{ top: 4, right: 6, bottom: 0, left: 0 }}>
          {tooltip}
          <Line
            type="monotone"
            {...seriesName}
            dataKey={props.yKey as DataKey<TRow>}
            stroke={color}
            strokeWidth={2}
            connectNulls={false}
            dot={(dotProps) => {
              const { cx, cy, index, key } = dotProps as {
                cx?: number;
                cy?: number;
                index: number;
                key?: string;
              };
              if (index !== lastIndex || cx == null || cy == null) {
                return <g key={key} />;
              }
              return <circle key={key} cx={cx} cy={cy} r={4} fill={color} stroke="none" />;
            }}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={props.height ?? 240}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis dataKey={props.xKey as DataKey<TRow>} stroke="var(--color-muted)" />
        <YAxis stroke="var(--color-muted)" tickFormatter={tickFormatter} width="auto" />
        {tooltip}
        <Line
          type="monotone"
          {...seriesName}
          dataKey={props.yKey as DataKey<TRow>}
          stroke={color}
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
